import React, { useState } from 'react';
import { useFormik } from 'formik';
import { confirmResetPassword } from 'aws-amplify/auth';
import { TextField, Box, Typography, Alert } from '@mui/material';
import useMediaQuery from '@mui/material/useMediaQuery';

import { resetPasswordSchema } from '../../validations/user';
import PrimaryBlueButton from '../common/buttons/PrimaryBlueButton';
import { useNavigate } from 'react-router-dom';
import BorderedButton from '../common/buttons/BorderedButton';
import { useThemeContext } from '../../contexts/BaseThemeContextProvider';
import PrimaryUnderlinedLink from '../common/PrimaryUnderlinedLink';

const ResetPasswordConfirmation: React.FC = () => {
  const [resetComplete, setResetComplete] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { theme } = useThemeContext();
  const md = useMediaQuery(theme.breakpoints.up('sm'));
  const navigate = useNavigate();

  const onHandleSubmit = async (values: any) => {
    try {
      await confirmResetPassword({
        username: values.email,
        confirmationCode: values.code,
        newPassword: values.newPassword,
      });
      setResetComplete(true);
      setError(null);
    } catch (err) {
      setError('Failed to reset password. Please check your inputs and try again.');
      console.error('Confirm reset password error:', err);
    }
  };

  const formik = useFormik({
    validateOnMount: true,
    initialValues: {
      email: '',
      code: '',
      newPassword: '',
      confirmPassword: '',
    },
    validationSchema: resetPasswordSchema,
    onSubmit: onHandleSubmit,
  });

  const renderResetPasswordComplete = () => {
    if (!resetComplete) return;

    return (
      <Box>
        <Typography variant="h6" sx={{ color: theme.custom.components.resetPasswordConfirmation.typography.color }}>
          Password Reset Successful
        </Typography>
        <Typography sx={{ color: theme.custom.components.resetPasswordConfirmation.typography.color }}>
          Your password has been successfully reset. You can now log in with your new password.
        </Typography>
        <Box>
          <PrimaryUnderlinedLink navigateTo="/login" text="Return to login" />
        </Box>
      </Box>
    );
  };

  return (
    <Box sx={{ display: 'flex', textAlign: 'center', py: 4, backgroundColor: theme.custom.components.resetPasswordConfirmation.backgroundColor, borderRadius: '16px' }}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          maxWidth: 800,
          margin: 'auto',
          mt: 0,
          padding: '0px 30px',
          textAlign: 'left',
        }}
      >
        <Box>
          {renderResetPasswordComplete()}
          <Typography variant="h6" sx={{ color: theme.custom.components.resetPasswordConfirmation.typography.color }}>
            Reset Your Password
          </Typography>
          {error && <Alert severity="error">{error}</Alert>}
          <TextField
            margin="normal"
            required
            fullWidth
            id="email"
            label="Email Address"
            autoComplete="email"
            {...formik.getFieldProps('email')}
            error={formik.touched.email && Boolean(formik.errors.email)}
            helperText={formik.touched.email && formik.errors.email}
          />
          <TextField
            margin="normal"
            required
            fullWidth
            id="code"
            label="Verification Code"
            {...formik.getFieldProps('code')}
            error={formik.touched.code && Boolean(formik.errors.code)}
            helperText={formik.touched.code && formik.errors.code}
          />
          <TextField
            margin="normal"
            required
            fullWidth
            id="newPassword"
            label="New Password"
            type="password"
            {...formik.getFieldProps('newPassword')}
            error={formik.touched.newPassword && Boolean(formik.errors.newPassword)}
            helperText={formik.touched.newPassword && formik.errors.newPassword}
          />
          <TextField
            margin="normal"
            required
            fullWidth
            id="confirmPassword"
            label="Confirm New Password"
            type="password"
            {...formik.getFieldProps('confirmPassword')}
            error={formik.touched.confirmPassword && Boolean(formik.errors.confirmPassword)}
            helperText={formik.touched.confirmPassword && formik.errors.confirmPassword}
          />
        </Box>
        <Box
          sx={{
            mt: 2,
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            justifyContent: { xs: 'center', sm: 'flex-start' },
            width: '100%',
            maxWidth: { xs: '100%', sm: '100%' },
          }}
        >
          <PrimaryBlueButton
            isLoading={formik.isSubmitting}
            onClick={() => formik.handleSubmit()}
            buttonText="Reset Password"
            additionalProps={{
              disabled: !formik.isValid || formik.isSubmitting,
            }}
          />
          <BorderedButton
            sxProps={{
              height: '41px',
              marginLeft: { xs: '0', sm: '10px' },
              marginTop: { xs: '10px', sm: '0' },
            }}
            onClick={() => navigate('/')}
            buttonText={'Cancel'}
          />
        </Box>
      </Box>
      {md && (
        <Box sx={{ maxWidth: '492px', maxHeight: '360px', mr: '30px' }}>
          <img src="/images/login.png" alt="Login" style={{ maxWidth: '492px', maxHeight: '360px' }} />
        </Box>
      )}
    </Box>
  );
};

export default ResetPasswordConfirmation;
